import React, { useState, useEffect } from 'react';
import { pipelineService } from '@/lib/pipelineService';
import type { DealActivity } from './types';
import { MessageSquare, Phone, Mail, Users, GitBranch, BarChart2, Plus, Send, MoreHorizontal, Pencil, Trash2, Filter, ArrowDownUp } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';

interface ActivityTimelineProps {
  dealId: string;
  currentUserId: string;
}

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  note: <MessageSquare className="w-4 h-4" />,
  call: <Phone className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  meeting: <Users className="w-4 h-4" />,
  stage_change: <GitBranch className="w-4 h-4" />,
  status_change: <BarChart2 className="w-4 h-4" />,
  value_change: <BarChart2 className="w-4 h-4" />,
  created: <Plus className="w-4 h-4" />,
};

const ACTIVITY_COLORS: Record<string, string> = {
  note: 'bg-blue-500/20 text-blue-400',
  call: 'bg-green-500/20 text-green-400',
  email: 'bg-purple-500/20 text-purple-400',
  meeting: 'bg-yellow-500/20 text-yellow-400',
  stage_change: 'bg-workspace-accent/20 text-workspace-accent',
  status_change: 'bg-orange-500/20 text-orange-400',
  value_change: 'bg-emerald-500/20 text-emerald-400',
  created: 'bg-muted text-muted-foreground',
};

const NOTE_TYPES = [
  { value: 'note', label: 'Note' },
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
] as const;

const MANUAL_TYPES = ['note', 'call', 'email', 'meeting'];

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ dealId, currentUserId }) => {
  const [activities, setActivities] = useState<DealActivity[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Note Creation State
  const [noteType, setNoteType] = useState<'note' | 'call' | 'email' | 'meeting'>('note');
  const [noteContent, setNoteContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filters & Pagination State
  const [visibleCount, setVisibleCount] = useState(3);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [filterType, setFilterType] = useState<'all' | 'note' | 'call' | 'email' | 'meeting'>('all');

  // Edit State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await pipelineService.getActivities(dealId);
      setActivities(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [dealId]); // eslint-disable-line

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    try {
      setSubmitting(true);
      await pipelineService.addActivity(dealId, currentUserId, noteType, noteContent.trim());
      setNoteContent('');
      setVisibleCount(3); // Reset to show the newest activity
      setSortOrder('desc'); // Ensure we can see the newly added note at top
      await loadActivities();
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditActivity = async (id: string) => {
    if (!editContent.trim()) return;
    try {
      setEditSubmitting(true);
      await pipelineService.updateActivity(id, editContent.trim());
      setEditingId(null);
      await loadActivities();
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteActivity = async (id: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) return;
    try {
      await pipelineService.deleteActivity(id);
      await loadActivities();
    } catch (err) {
      console.error('Failed to delete activity:', err);
    }
  };

  const timeAgo = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredActivities = activities.filter(a => {
    if (filterType !== 'all' && a.type !== filterType) return false;
    return true;
  });

  const sortedActivities = [...filteredActivities].sort((a, b) => {
    const tA = new Date(a.created_at).getTime();
    const tB = new Date(b.created_at).getTime();
    return sortOrder === 'desc' ? tB - tA : tA - tB;
  });

  const visibleActivities = sortedActivities.slice(0, visibleCount);
  const hasMore = visibleCount < sortedActivities.length;

  return (
    <div className="space-y-6">
      {/* Add note */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3 shadow-sm">
        {/* Type tabs */}
        <div className="flex gap-1">
          {NOTE_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setNoteType(t.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                noteType === t.value
                  ? 'bg-workspace-accent text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <Textarea
          placeholder={`Add a ${noteType}...`}
          value={noteContent}
          onChange={e => setNoteContent(e.target.value)}
          className="bg-background border-input resize-none text-sm focus-visible:ring-workspace-accent/30"
          rows={3}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote();
          }}
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground hidden sm:inline-block">
            Press <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted text-[10px] mx-0.5 font-mono">⌘</kbd> + <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted text-[10px] mx-0.5 font-mono">Enter</kbd> to save
          </span>
          <Button
            size="sm"
            onClick={handleAddNote}
            disabled={!noteContent.trim() || submitting}
            className="bg-workspace-accent text-white hover:bg-workspace-accent-hover h-8 px-4 gap-1.5 ml-auto shadow-sm"
          >
            <Send className="w-3.5 h-3.5" />
            {submitting ? 'Saving...' : 'Add Activity'}
          </Button>
        </div>
      </div>

      {/* Timeline Header & Filters */}
      <div className="flex items-center justify-between pt-2">
        <h4 className="text-sm font-semibold text-foreground">Timeline</h4>
        <div className="flex items-center gap-2">
          {/* Filter Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs bg-card border-border hover:bg-muted text-muted-foreground hover:text-foreground capitalize">
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                {filterType === 'all' ? 'All Activities' : filterType + 's'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Filter By Type</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => { setFilterType('all'); setVisibleCount(3); }}
                className={filterType === 'all' ? 'bg-muted/50 font-medium' : ''}
              >
                All Activities
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { setFilterType('note'); setVisibleCount(3); }}
                className={filterType === 'note' ? 'bg-muted/50 font-medium' : ''}
              >
                Notes
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { setFilterType('call'); setVisibleCount(3); }}
                className={filterType === 'call' ? 'bg-muted/50 font-medium' : ''}
              >
                Calls
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { setFilterType('email'); setVisibleCount(3); }}
                className={filterType === 'email' ? 'bg-muted/50 font-medium' : ''}
              >
                Emails
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { setFilterType('meeting'); setVisibleCount(3); }}
                className={filterType === 'meeting' ? 'bg-muted/50 font-medium' : ''}
              >
                Meetings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sort Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs bg-card border-border hover:bg-muted text-muted-foreground hover:text-foreground">
                <ArrowDownUp className="w-3.5 h-3.5 mr-1.5" />
                {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Sort Order</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => { setSortOrder('desc'); setVisibleCount(3); }}
                className={sortOrder === 'desc' ? 'bg-muted/50 font-medium' : ''}
              >
                Newest First
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { setSortOrder('asc'); setVisibleCount(3); }}
                className={sortOrder === 'asc' ? 'bg-muted/50 font-medium' : ''}
              >
                Oldest First
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Timeline List */}
      {loading ? (
        <div className="text-center py-8 text-sm text-muted-foreground">Loading activities...</div>
      ) : sortedActivities.length === 0 ? (
        <div className="text-center py-10 bg-muted/20 rounded-xl border border-dashed border-border/50 text-sm text-muted-foreground">
          No activities match your filters.
        </div>
      ) : (
        <div className="relative space-y-4">
          {/* Vertical line */}
          <div className="absolute left-[15px] top-4 bottom-0 w-px bg-border/60" />

          {visibleActivities.map(activity => (
            <div key={activity.id} className="flex gap-4 relative group">
              {/* Icon */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative z-10 shadow-sm ring-4 ring-background ${ACTIVITY_COLORS[activity.type] || 'bg-muted text-muted-foreground'}`}>
                {ACTIVITY_ICONS[activity.type] || <MessageSquare className="w-4 h-4" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 bg-card border border-border rounded-lg p-3 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-sm font-semibold text-foreground truncate">
                    {activity.user?.name || 'Unknown user'}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground shrink-0">
                      {timeAgo(activity.created_at)}
                    </span>

                    {/* Actions Dropdown for Manual Activities */}
                    {MANUAL_TYPES.includes(activity.type) && activity.user_id === currentUserId && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setEditingId(activity.id);
                            setEditContent(activity.content);
                          }}>
                            <Pencil className="w-3.5 h-3.5" />
                            Edit Note
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            destructive
                            onClick={() => handleDeleteActivity(activity.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete Note
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Edit Mode vs Read Mode */}
                {editingId === activity.id ? (
                  <div className="space-y-2 mt-2">
                    <Textarea
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      className="bg-background border-input text-sm resize-none focus-visible:ring-workspace-accent/30"
                      rows={3}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7 text-xs"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        className="h-7 text-xs bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                        disabled={!editContent.trim() || editSubmitting}
                        onClick={() => handleEditActivity(activity.id)}
                      >
                        {editSubmitting ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-foreground/90 whitespace-pre-wrap">{activity.content}</p>
                )}
              </div>
            </div>
          ))}

          {/* Load More Option */}
          {hasMore && (
            <div className="pt-4 pb-2 flex justify-center relative z-10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisibleCount(prev => prev + 5)}
                className="bg-card text-muted-foreground hover:text-foreground text-xs font-medium rounded-full px-6 border-border shadow-sm"
              >
                Load older activities
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
